import { z } from 'zod';
export const REPORT_SOURCES = {
    'fleet.summary': {
        label: 'Fleet operations summary',
        endpoint: 'GET /api/v1/fleet/summary',
        supportsClosedPeriod: true,
        withheldForClosedPeriod: ['openNow', 'criticalOpenNow', 'awaitingApproval'],
        /*
         * The same withholding, one level down.
         *
         * `mostIncidents[].stillOpen` is answered from the current incident status too,
         * and was missed on the first pass — found while writing the vehicle table, which
         * is exactly the sort of thing that reaches a customer as "why does last March say
         * three are still open".
         */
        withheldNestedForClosedPeriod: ['mostIncidents[].stillOpen'],
    },
};
export const REPORT_SOURCE_KEYS = Object.keys(REPORT_SOURCES);
/**
 * The parts of a source a template renders.
 *
 * Named sections rather than free-form field lists: this is what lets two templates
 * differ over one source without either of them computing anything. `incidents.period`
 * is the incident-centric selection of exactly the same response `fleet.summary`
 * renders in full.
 */
export const REPORT_SECTIONS = [
    /*
     * The period in prose, at the top.
     *
     * The only section whose text is not computed from the numbers directly below
     * it — it is written, either by the model or deterministically. It therefore
     * always states WHICH, because a reader deciding whether to trust a sentence
     * needs to know if a model wrote it (the same rule recommendations follow with
     * `explanationSource`).
     */
    'narrative',
    'headline',
    'incidentFlow',
    'bySeverity',
    'byType',
    'resolutionTime',
    'dailyTrend',
    'vehicleBreakdown',
    'fleetHealth',
];
export const REPORT_TEMPLATES = {
    'fleet.summary': {
        label: 'Fleet operations summary',
        description: 'The whole operations picture for the period: incidents raised and closed against the ' +
            'period before, severity and type breakdowns, resolution time, the daily trend, the ' +
            'vehicles with the most incidents, and how many vehicles were silent.',
        version: 1,
        source: 'fleet.summary',
        sections: [
            'headline',
            'incidentFlow',
            'bySeverity',
            'byType',
            'resolutionTime',
            'dailyTrend',
            'vehicleBreakdown',
            'fleetHealth',
        ],
        feature: null,
    },
    /*
     * The briefing. Same source as `fleet.summary` — the counts are already
     * computed and a second implementation of them is exactly the divergence D1
     * exists to prevent. It differs by leading with prose and carrying fewer
     * tables: a briefing is read on a phone at 07:00, not studied.
     */
    'fleet.briefing': {
        label: 'Fleet briefing',
        description: 'A short written brief for the period — what happened, what needs attention — followed ' +
            'by the headline figures behind it. The wording is AI-enhanced when the tenant has that ' +
            'enabled and falls back to deterministic text otherwise; the report says which it used.',
        version: 1,
        source: 'fleet.summary',
        sections: ['narrative', 'headline', 'incidentFlow', 'bySeverity', 'fleetHealth'],
        feature: null,
    },
    'incidents.period': {
        /*
         * The SAME source as `fleet.summary`, and that is the point.
         *
         * "Incidents raised, closed, by severity and type" is already computed by
         * `GET /fleet/summary` — opened, resolved, byRisk, byType, medianResolutionHours
         * and the daily series are all in its response, and none of them is truncated.
         * Adding an incidents-only aggregate would have been a second implementation of
         * the same counts, which is exactly the divergence D1 exists to prevent.
         *
         * So this template differs by what it RENDERS, not by what it computes: the
         * incident story without the fleet-inventory framing.
         */
        label: 'Incident summary',
        description: 'Incidents raised and closed in the period, broken down by severity and type, with ' +
            'resolution time and the daily trend. Silent vehicles are reported beside the counts, ' +
            'because a vehicle sending no telemetry raises no incidents and would otherwise look ' +
            'like a clean record.',
        version: 1,
        source: 'fleet.summary',
        sections: [
            'headline',
            'incidentFlow',
            'bySeverity',
            'byType',
            'resolutionTime',
            'dailyTrend',
            'fleetHealth',
        ],
        feature: null,
    },
};
export const REPORT_TEMPLATE_KEYS = Object.keys(REPORT_TEMPLATES);
export const reportTemplateKeySchema = z.enum(REPORT_TEMPLATE_KEYS);
/**
 * How often a report runs.
 *
 * Mirrors `REPORT_SCHEDULE_KINDS` in `@oip/domain`, which cannot import this package.
 * A test asserts the two agree — the same arrangement the alerting vocabularies use.
 *
 * Cron is deliberately absent (D10).
 */
export const reportScheduleKindSchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY']);
/**
 * A schedule, checked as a whole rather than field by field.
 *
 * WEEKLY without a weekday and MONTHLY without a day are not merely incomplete —
 * they are ambiguous, and defaulting them would invent a schedule nobody chose.
 */
export const reportScheduleSchema = z
    .object({
    kind: reportScheduleKindSchema,
    /** Local wall-clock time, resolved in the tenant's zone. */
    localTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm, 24-hour.'),
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.kind === 'WEEKLY' && (v.weekday === undefined || v.weekday === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['weekday'],
            message: 'Choose a day of the week.',
        });
    }
    if (v.kind === 'MONTHLY' && (v.dayOfMonth === undefined || v.dayOfMonth === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dayOfMonth'],
            message: 'Choose a day of the month.',
        });
    }
});
/**
 * Delivered formats.
 *
 * PDF is included at launch by decision, so D19's bound on HOW it is produced is the
 * live constraint: a declarative library rendering from the same structured data the
 * API returns, never a headless browser. Chromium in the worker would be a large
 * dependency, a sandbox surface, and memory behaviour competing with telemetry
 * ingestion — and ingestion must not slow down because somebody scheduled a report.
 */
export const REPORT_FORMATS = ['CSV', 'HTML', 'PDF'];
export const reportFormatSchema = z.enum(REPORT_FORMATS);
// ── API ─────────────────────────────────────────────────────────────────────
/**
 * External report recipients — an address with no account, under D23.
 *
 * D22 made recipients memberships only, and the reason was revocation: a typed
 * address lets tenant data leave to somebody with no account, no membership and
 * no way to switch them off. Off-boarding a member stops their reports; there
 * is nothing equivalent for an address.
 *
 * D23 permits the exception — boards, auditors and insurers are a real
 * workflow — but only with the revocation story rebuilt out of other parts:
 *
 *  - **Verification.** The address proves it exists and is being read before it
 *    receives anything, so a typo cannot silently subscribe a stranger.
 *  - **A mandatory expiry, and no "never".** Re-approval IS the revocation
 *    mechanism. Anything else relies on somebody remembering to remove a
 *    person years later, which is the failure mode every external-share
 *    feature eventually has.
 *  - **Audited approval**, naming the administrator who added it.
 *  - **Its own entitlement**, so a deployment can refuse the capability.
 */
/**
 * Additional addresses per report.
 *
 * Small on purpose. This is "the auditor and the broker", not a mailing list —
 * a distribution list is somebody else's product, and every address here is a
 * copy of tenant data outside the tenant.
 */
export const MAX_EXTERNAL_RECIPIENTS_PER_REPORT = 4;
/** The longest an address may keep receiving before somebody re-approves it. */
export const EXTERNAL_RECIPIENT_MAX_DAYS = 365;
/** How long a verification code is worth trying. */
export const EXTERNAL_RECIPIENT_CODE_TTL_MINUTES = 30;
/** Wrong codes tolerated before the address must be re-added. */
export const EXTERNAL_RECIPIENT_MAX_ATTEMPTS = 5;
export const EXTERNAL_RECIPIENT_STATUSES = ['PENDING', 'VERIFIED', 'EXPIRED'];
export const externalRecipientStatusSchema = z.enum(EXTERNAL_RECIPIENT_STATUSES);
export const externalRecipientSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    /**
     * Derived from `verifiedAt` and `expiresAt` on read, never stored. Two fields
     * that must agree with a third eventually disagree with it.
     */
    status: externalRecipientStatusSchema,
    verifiedAt: z.string().datetime().nullable(),
    /** Always set. There is deliberately no unlimited option. */
    expiresAt: z.string().datetime(),
    /** Who approved it, so the audit answer is on the screen and not only in the log. */
    addedByName: z.string().nullable(),
    addedAt: z.string().datetime(),
    /**
     * Null once verified. While pending it says whether the code is still worth
     * entering — an expired code and a wrong one need different next steps.
     */
    codeExpiresAt: z.string().datetime().nullable(),
    attemptsRemaining: z.number().int().nullable(),
});
export const addExternalRecipientSchema = z
    .object({
    email: z.string().trim().toLowerCase().email().max(254),
    /**
     * Days until it stops receiving. Bounded at both ends: unbounded is refused
     * by D23, and zero would add an address already expired.
     */
    days: z.number().int().min(1).max(EXTERNAL_RECIPIENT_MAX_DAYS),
})
    .strict();
export const verifyExternalRecipientSchema = z
    .object({ code: z.string().trim().min(4).max(12) })
    .strict();
/**
 * What the send attempt did, reported rather than assumed.
 *
 * `SENT` is a claim about handing the message to a transport, not about
 * arrival — the same honesty the alerting design applies to verification codes
 * (D12a), which never enter the database or a log.
 */
export const addExternalRecipientResponseSchema = z.object({
    recipient: externalRecipientSchema,
    codeDelivery: z.enum(['SENT', 'NOT_CONFIGURED', 'FAILED']),
});
export const scheduledReportSchema = z.object({
    id: z.string().uuid(),
    templateKey: reportTemplateKeySchema,
    /** From the registry, so the list reads as documents rather than as keys. */
    templateLabel: z.string(),
    format: reportFormatSchema,
    schedule: reportScheduleSchema,
    /**
     * The schedule as a sentence.
     *
     * Rendered by the server from the same resolver the worker schedules with, so the
     * screen cannot describe one thing while the job does another. It is also the whole
     * reason cron is refused (D10) — this is the check a person can actually perform.
     */
    scheduleDescription: z.string(),
    enabled: z.boolean(),
    /** Resolved in the tenant's zone. Null until a schedule has been resolved. */
    nextRunAt: z.string().datetime().nullable(),
    lastRunAt: z.string().datetime().nullable(),
    artifactRetentionDays: z.number().int(),
    recipientUserIds: z.array(z.string().uuid()),
    /** Members named as recipients who can no longer sign in, so nothing is silent. */
    inactiveRecipients: z.number().int(),
    /**
     * Addresses outside the organisation (D23). Empty unless the tenant holds
     * `REPORT_EXTERNAL_RECIPIENTS`, so a screen without the entitlement shows no
     * trace of a capability it does not have.
     */
    externalRecipients: z.array(externalRecipientSchema),
    /** The cap, so the UI states the rule rather than hard-coding a second copy of it. */
    externalRecipientLimit: z.number().int(),
});
export const scheduledReportsResponseSchema = z.object({
    items: z.array(scheduledReportSchema),
    /** The tenant's zone, so the UI can say which clock the times are on. */
    timeZone: z.string().nullable(),
    /** The cap, and how close this tenant is to it (D31). */
    limit: z.number().int(),
});
export const createScheduledReportSchema = z
    .object({
    templateKey: reportTemplateKeySchema,
    format: reportFormatSchema,
    schedule: reportScheduleSchema,
    /**
     * Memberships, never addresses (D22). At least one, because a report with no
     * audience is a job that burns resources and reaches nobody.
     */
    recipientUserIds: z.array(z.string().uuid()).min(1).max(100),
    artifactRetentionDays: z.number().int().min(30).max(3650).optional(),
})
    .strict();
export const updateScheduledReportSchema = createScheduledReportSchema
    .partial()
    .extend({ enabled: z.boolean().optional() })
    .strict();
export const REPORT_RUN_STATUSES = ['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED'];
export const reportRunStatusSchema = z.enum(REPORT_RUN_STATUSES);
export const scheduledReportRunSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(['SCHEDULED', 'MANUAL', 'TEST']),
    status: reportRunStatusSchema,
    windowStart: z.string().datetime(),
    windowEnd: z.string().datetime(),
    templateVersion: z.number().int(),
    /** What produced the numbers, as opposed to what they looked like (D2). */
    generatorVersion: z.string(),
    attempts: z.number().int(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    /** The failure in the system's own words, so it is diagnosable rather than red. */
    error: z.string().nullable(),
    /** Null once retention has removed the bytes; the run row survives it (D18). */
    artifact: z
        .object({
        format: reportFormatSchema,
        sizeBytes: z.number().int(),
        expiresAt: z.string().datetime(),
    })
        .nullable(),
});
export const scheduledReportRunsResponseSchema = z.object({
    items: z.array(scheduledReportRunSchema),
});
/** A short-lived download, never a permanent or public URL (D21). */
export const reportDownloadSchema = z.object({
    url: z.string(),
    expiresInSeconds: z.number().int(),
    fileName: z.string(),
});
//# sourceMappingURL=reports.js.map